import Keycloak from 'keycloak-js'
import React, { useEffect, useMemo, useState } from 'react'

import KeycloakAuthClient from './KeycloakAuth'

export interface AuthContext {
  isAdmin: boolean | undefined
  isAmbassador: boolean | undefined
  isNutritionist: boolean | undefined
  hasRole(role: string): boolean
  externalID: string | undefined
  logout(): void
  dbContext: string | undefined
  email: string | undefined
}

const AuthContext = React.createContext<AuthContext | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [keycloak, setKeycloak] = useState<Keycloak | null>()

  useEffect(() => {
    const instance = KeycloakAuthClient.getKeycloakInstance()

    setKeycloak(instance)
  }, [])

  const authContext = useMemo(
    () => ({
      isAdmin: keycloak?.realmAccess?.roles.includes('admin'),
      isAmbassador: keycloak?.realmAccess?.roles.includes('ambassador'),
      isNutritionist: keycloak?.realmAccess?.roles.includes('nutritionist'),
      externalID: keycloak?.tokenParsed?.sub,
      dbContext: keycloak?.tokenParsed?.dbContext?.toUpperCase() as string | undefined,
      email: keycloak?.tokenParsed?.email,
      logout: () => {
        keycloak?.logout()
      },
      hasRole(role: string) {
        return keycloak?.realmAccess?.roles.includes(role) || false
      },
    }),
    [keycloak],
  )

  if (!keycloak) {
    // The app is only rendered after a successful Keycloak login
    // (see src/index.tsx), so the instance is always available. Waiting here
    // ensures role-based guards never run against an auth context without
    // roles.
    return null
  }

  return <AuthContext.Provider value={authContext}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = React.useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

/**
 * Whether the current user holds a role that grants access to this app's
 * APIs (admin, nutritionist or ambassador). Most backend endpoints
 * (recipes, tags, food restrictions, authors, ...) respond with 403
 * otherwise.
 */
export function isPrivilegedUser(auth: AuthContext | null): boolean {
  return !!auth && !!(auth.isAdmin || auth.isNutritionist || auth.isAmbassador)
}

export const KeycloakAuth = KeycloakAuthClient
